/**
 * Cross-origin image shield (I-015g) — runs once at widget init, before
 * rrweb starts recording.
 *
 * Background: rrweb-snapshot, when `inlineImages: true`, mutates each
 * host-page <img> element directly:
 *
 *     image.crossOrigin = 'anonymous'
 *     canvasCtx.drawImage(image, 0, 0)
 *     attributes.rr_dataURL = canvas.toDataURL(...)
 *     image.removeAttribute('crossorigin')
 *
 * The `crossOrigin = 'anonymous'` assignment causes the browser to
 * re-issue the image fetch in CORS mode. For cross-origin hosts that
 * don't return `Access-Control-Allow-Origin`, the browser blocks the
 * fetch *and caches the failed response*, so the page's plain <img>
 * also stops rendering until a hard reload that purges the cache.
 *
 * This shield walks the DOM at startup and overrides the `crossOrigin`
 * setter to a no-op for every cross-origin <img>. rrweb's mutation
 * becomes a no-op, the image's load isn't poisoned, and:
 *
 *   - Same-origin images: untouched, still inline normally for the
 *     BB Replayer.
 *   - Cross-origin images: rrweb's canvas.drawImage taints the canvas
 *     (no CORS opt-in), toDataURL throws, rrweb catches and logs
 *     "Cannot inline img src=..." — exactly its existing fallback. The
 *     BB Replayer renders these images via direct load (display-only
 *     <img> doesn't need CORS).
 *
 * A MutationObserver catches dynamically-added images. The shield
 * itself is idempotent — running on an already-shielded element is
 * a no-op.
 */

const SHIELDED = new WeakSet<HTMLImageElement>()

function isCrossOrigin(rawSrc: string): boolean {
  if (!rawSrc) return false
  try {
    const u = new URL(rawSrc, location.href)
    return u.origin !== location.origin
  } catch {
    return false
  }
}

function shieldImage(img: HTMLImageElement): void {
  if (SHIELDED.has(img)) return
  if (!isCrossOrigin(img.src)) return
  SHIELDED.add(img)
  try {
    Object.defineProperty(img, 'crossOrigin', {
      set: () => { /* no-op — block rrweb's mutation */ },
      get: () => null,
      configurable: true,
    })
  } catch {
    // Some browsers / CSP modes refuse defineProperty on element
    // properties. We accept that — the worst case is the original bug
    // surfaces for that one element, no new failure mode.
  }
}

/**
 * Install the cross-origin <img> shield. Returns a teardown function
 * that disconnects the MutationObserver — call from widget cleanup.
 */
export function shieldCrossOriginImages(): () => void {
  // Initial pass over images already in the DOM.
  document.querySelectorAll('img').forEach((el) => shieldImage(el as HTMLImageElement))

  // Catch dynamically-added images.
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node instanceof HTMLImageElement) {
          shieldImage(node)
        } else if (node instanceof Element) {
          node.querySelectorAll('img').forEach((el) => shieldImage(el as HTMLImageElement))
        }
      }
    }
  })

  if (document.documentElement) {
    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  return () => observer.disconnect()
}
